import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { Announcement } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { whatsappShare } from '../../shared/whatsapp';

const when = (iso: string) => new Date(iso).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });

/** Avisos de la Iglesia (Motor 10). Publicar notifica a todos (Motor 11). */
export function AnnouncementsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canSend = me.can('communication.send');
  const list = useAsync(
    (signal) => (churchId ? api.get<{ items: Announcement[] }>(`/communication/churches/${churchId}/announcements`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Avisos</h1>
        <p className="muted">Lo que la iglesia comparte con todos.</p>
      </header>
      {canSend ? <ComposeCard churchId={churchId} onPublished={list.reload} /> : null}
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && list.data.length === 0 ? <EmptyState title="Aún no hay avisos." /> : null}
      {list.status === 'ready'
        ? list.data.map((a) => <AnnouncementCard key={a.id} churchId={churchId} announcement={a} canArchive={canSend} onArchived={list.reload} />)
        : null}
    </div>
  );
}

function AnnouncementCard({ churchId, announcement: a, canArchive, onArchived }: { churchId: string; announcement: Announcement; canArchive: boolean; onArchived: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function archive() {
    if (!window.confirm(`¿Archivar «${a.title}»? Ya no aparecerá en Avisos.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/communication/churches/${churchId}/announcements/${a.id}/archive`);
      onArchived();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card
      title={a.title}
      action={
        canArchive ? (
          <Button variant="ghost" busy={busy} onClick={archive}>
            Archivar
          </Button>
        ) : undefined
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      <p className="announcement__body">{a.body}</p>
      <p className="muted small">
        {a.publishedBy.name} · {when(a.publishedAt)}
      </p>
      <a className="btn btn--secondary" href={whatsappShare(`*${a.title}*\n${a.body}\n\n${window.location.origin}/app/avisos`)} target="_blank" rel="noopener noreferrer">
        Enviar por WhatsApp
      </a>
    </Card>
  );
}

function ComposeCard({ churchId, onPublished }: { churchId: string; onPublished: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    if (!window.confirm('Se avisará a toda la iglesia. ¿Publicar?')) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.post(`/communication/churches/${churchId}/announcements`, { title: String(form.get('title')), body: String(form.get('body')) });
      formEl.reset();
      setDone(true);
      onPublished();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Nuevo aviso">
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {done ? <Alert tone="success">Aviso publicado. Todos lo verán en sus notificaciones.</Alert> : null}
        <Field label="Título" name="title" required minLength={3} maxLength={150} />
        <div className="field">
          <label htmlFor="announcement-body">Mensaje</label>
          <textarea id="announcement-body" name="body" required maxLength={5000} rows={4} />
        </div>
        <Button type="submit" busy={busy}>
          Publicar
        </Button>
      </form>
    </Card>
  );
}
